import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import AuthGuard from '@/components/AuthGuard'
import Login from '@/pages/Login'
import ReadinessOverview from '@/pages/ReadinessOverview'
import ObjectiveDetail from '@/pages/ObjectiveDetail'
import Progress from '@/pages/Progress'
import ProgressPrint from '@/pages/ProgressPrint'
import Catalogue from '@/pages/Catalogue'
import TechnologyDetail from '@/pages/TechnologyDetail'
import Scoring from '@/pages/Scoring'
import Report from '@/pages/Report'
import MigrationHub from '@/pages/MigrationHub'
import SubmissionPlan from '@/pages/SubmissionPlan'
import MigrationItems from '@/pages/MigrationItems'
import Roadmap from '@/pages/Roadmap'
import PlanPrint from '@/pages/PlanPrint'
import { useIsLocked, type GateId } from '@/stores/profileStore'

// Earlier bookmarks redirect to the Iteration 3 routes, query and hash kept.
function RedirectKeepSearch({ to }: { to: string }) {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}
function RedirectObjective() {
  const { code = '' } = useParams()
  return <RedirectKeepSearch to={`/readiness/${code}`} />
}
function RedirectPlanPrint() {
  const { versionId = '' } = useParams()
  return <RedirectKeepSearch to={`/readiness/submission/versions/${versionId}/print`} />
}

// POC route gates (I6 POC §7.4, Note 6): no-ops in Full. The objective page
// redirects to the Dashboard with that objective's drawer open; pages that
// only hang off a locked feature (its print view, a catalogue entry) go to
// the feature's own route, which shows the locked preview.
function ObjectiveRoute() {
  const { code = '' } = useParams()
  const { search } = useLocation()
  const locked = useIsLocked('objective_detail')
  if (!locked) return <ObjectiveDetail />
  const sp = new URLSearchParams(search)
  sp.delete('from')
  sp.delete('return')
  return <Navigate to={`/readiness?${sp.toString()}`} replace state={{ openObjective: code }} />
}
function GatedRoute({ gate, to, children }: { gate: GateId; to: string; children: JSX.Element }) {
  const locked = useIsLocked(gate)
  return locked ? <RedirectKeepSearch to={to} /> : children
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        {/* Default landing = the Readiness Dashboard (I6 §14): a replace redirect before
            first paint, query and hash kept, so Back leaves the site rather than looping. */}
        <Route index element={<RedirectKeepSearch to="/readiness" />} />
        {/* Readiness module (Iteration 3 §1.2): five features, all routes take ?index={index_id}.
            Objective pages sit at /readiness/{code} under Overview. */}
        <Route path="readiness" element={<ReadinessOverview />} />
        <Route path="readiness/progress" element={<Progress />} />
        <Route path="readiness/progress/print" element={<GatedRoute gate="progress" to="/readiness/progress"><ProgressPrint /></GatedRoute>} />
        <Route path="readiness/submission" element={<MigrationHub />} />
        <Route path="readiness/submission/plan" element={<SubmissionPlan />} />
        <Route path="readiness/submission/versions/:versionId/print" element={<PlanPrint />} />
        <Route path="readiness/tracker" element={<MigrationItems />} />
        <Route path="readiness/tracker/roadmap" element={<Roadmap />} />
        <Route path="readiness/catalogue" element={<Catalogue />} />
        <Route path="readiness/catalogue/:id" element={<GatedRoute gate="technology_catalogue" to="/readiness/catalogue"><TechnologyDetail /></GatedRoute>} />
        <Route path="readiness/scoring" element={<Scoring />} />
        <Route path="readiness/report/:snapshotId" element={<Report />} />
        {/* Settings is coming soon (I6 §15): its route redirects to the Dashboard until it ships. */}
        <Route path="readiness/settings" element={<RedirectKeepSearch to="/readiness" />} />
        {/* Earlier routes */}
        <Route path="readiness/objectives/:code" element={<RedirectObjective />} />
        <Route path="readiness/assessments" element={<RedirectKeepSearch to="/readiness/progress" />} />
        <Route path="readiness/snapshots" element={<RedirectKeepSearch to="/readiness/progress" />} />
        <Route path="readiness/migration" element={<RedirectKeepSearch to="/readiness/submission" />} />
        <Route path="readiness/migration/plan" element={<RedirectKeepSearch to="/readiness/submission/plan" />} />
        <Route path="readiness/migration/plan/versions/:versionId/print" element={<RedirectPlanPrint />} />
        <Route path="readiness/migration/items" element={<RedirectKeepSearch to="/readiness/tracker" />} />
        <Route path="readiness/migration/roadmap" element={<RedirectKeepSearch to="/readiness/tracker/roadmap" />} />
        <Route path="readiness/export" element={<RedirectKeepSearch to="/readiness/submission/plan" />} />
        <Route path="readiness/export/:draftId" element={<RedirectKeepSearch to="/readiness/submission/plan" />} />
        <Route path="readiness/:code" element={<ObjectiveRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export default App
